/**
 * Iter123 — Admin Tips Queue.
 *
 * Admin review interface for tips submitted through /tip.
 * Actions: promote to case, dismiss, add review notes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { Shield, RefreshCw, CheckCircle2, XCircle, Clock, ArrowLeft } from 'lucide-react';

const STATUSES = ['ALL', 'NEW', 'REVIEWING', 'PROMOTED', 'DISMISSED'];
const dt = (v) => v ? new Date(v).toLocaleString() : '—';

export default function AdminTipsQueue() {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('NEW');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [caseIdField, setCaseIdField] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'ALL' ? `${BACKEND_URL}/api/tips` : `${BACKEND_URL}/api/tips?status=${statusFilter}`;
      const r = await fetch(url, { headers: H });
      setTips(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [statusFilter, token]);

  useEffect(() => { load(); }, [load]);

  const review = async (action) => {
    if (!selected) return;
    const r = await fetch(`${BACKEND_URL}/api/tips/${selected.id}/review`, {
      method: 'POST', headers: JH,
      body: JSON.stringify({ action, notes, case_id: caseIdField }),
    });
    if (!r.ok) return toast({ title: 'Failed', variant: 'destructive' });
    toast({ title: `Tip ${action.toLowerCase()}` });
    setSelected(null); setNotes(''); setCaseIdField('');
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-tips-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = '/dashboard'))}
                  className="text-[#94A3B8] hover:text-white" data-testid="tips-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="w-12 h-12 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <Shield className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <div className="text-xs text-amber-300 uppercase tracking-wider font-mono">Admin · Intel Queue</div>
            <h1 className="text-2xl font-bold text-white">Public Tip Portal — Review Queue</h1>
            <p className="text-slate-400 text-sm">Anonymous tips submitted through the public portal.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-32" data-testid="tips-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="ghost" onClick={load} className="text-slate-300"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader><CardTitle className="text-sm text-white">Tips ({tips.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
            {tips.length === 0 ? <div className="text-xs text-slate-500 text-center py-6">No tips.</div> : tips.map(t => (
              <button key={t.id} onClick={() => { setSelected(t); setNotes(t.review_notes || ''); }}
                className={`w-full text-left p-2 rounded border ${selected?.id === t.id ? 'bg-amber-900/40 border-amber-500' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}
                data-testid={`admin-tip-row-${t.id}`}>
                <div className="text-sm text-white truncate">{t.category?.replace(/_/g, ' ')}</div>
                <div className="text-xs text-slate-400 truncate">{t.summary}</div>
                <div className="text-[10px] text-slate-500 mt-1 flex items-center justify-between">
                  <span><Clock className="w-3 h-3 inline mr-0.5" /> {dt(t.submitted_at)}</span>
                  <Badge className={`text-[9px] ${t.status === 'NEW' ? 'bg-emerald-500/20 text-emerald-300' : t.status === 'PROMOTED' ? 'bg-blue-500/20 text-blue-300' : t.status === 'DISMISSED' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>{t.status}</Badge>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-[#131A2A] border-[#334155] lg:col-span-2">
          <CardHeader><CardTitle className="text-sm text-white">Tip Detail</CardTitle></CardHeader>
          <CardContent>
            {!selected ? <div className="text-sm text-slate-500 text-center py-8">Select a tip to review.</div> : (
              <div className="space-y-3 text-sm text-slate-200">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><b>Category:</b> {selected.category?.replace(/_/g, ' ')}</div>
                  <div><b>Submitted:</b> {dt(selected.submitted_at)}</div>
                  <div><b>Anonymous:</b> {selected.anonymous ? 'Yes' : 'No'}</div>
                  <div><b>IP:</b> <span className="font-mono">{selected.ip_address || '—'}</span></div>
                  {!selected.anonymous && (<>
                    <div><b>Name:</b> {selected.reporter_name || '—'}</div>
                    <div><b>Contact:</b> {selected.reporter_email || selected.reporter_phone || '—'}</div>
                  </>)}
                  <div><b>Subject:</b> {selected.subject_name || '—'}</div>
                  <div><b>Location:</b> {selected.incident_location || '—'}</div>
                  <div className="col-span-2"><b>When:</b> {selected.incident_when || '—'}</div>
                  {selected.subject_description && <div className="col-span-2"><b>Description:</b> {selected.subject_description}</div>}
                </div>
                <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
                  <div className="text-[10px] text-slate-400 uppercase font-mono mb-1">Summary</div>
                  <div className="text-white text-sm whitespace-pre-wrap">{selected.summary}</div>
                </div>
                <Input value={caseIdField} onChange={e => setCaseIdField(e.target.value)}
                  placeholder="Optional: existing case ID to attach on promote"
                  className="bg-slate-800 border-slate-700 text-white" data-testid="tip-case-id" />
                <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Review notes" className="bg-slate-800 border-slate-700 text-white" rows={2} data-testid="tip-notes-input" />
                <div className="flex gap-2">
                  <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => review('PROMOTE')} data-testid="tip-promote-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Promote
                  </Button>
                  <Button variant="outline" className="border-amber-500/40 !text-amber-300" onClick={() => review('REVIEW')} data-testid="tip-review-btn">
                    Mark Reviewing
                  </Button>
                  <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={() => review('DISMISS')} data-testid="tip-dismiss-btn">
                    <XCircle className="w-4 h-4 mr-1" /> Dismiss
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
